import { useEffect, useId, useRef } from "react";
import { ShieldAlertIcon, ShieldCheckIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { TurnstileHandle } from "@/lib/use-turnstile";

interface TurnstileGateProps {
  turnstile: TurnstileHandle;
  /** What the visitor is trying to do, for the sentence that explains the wait. */
  toolName?: string;
}

/**
 * The challenge's one visible home. Most visits solve invisibly and never see
 * this. When Cloudflare wants the visitor to do something, or the challenge
 * cannot complete at all, it covers the page: with the widget in the first
 * case, and with the way out in the second. Either way the tool is unusable
 * until the challenge passes, so a line under the panes is not enough.
 *
 * Always mounted, hidden with visibility rather than unmounted. Turnstile
 * renders an iframe into the container, and an iframe that leaves the DOM
 * reloads, which destroys the widget along with any challenge in progress.
 */
export function TurnstileGate({
  turnstile,
  toolName = "this tool",
}: TurnstileGateProps) {
  const titleId = useId();
  const bodyId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  const open = turnstile.enabled && turnstile.challenge !== "idle";
  const failed = turnstile.challenge === "failed";

  // Keyboard and screen-reader users are otherwise left on the disabled
  // controls underneath, with no announcement that the page changed.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!turnstile.enabled) return null;

  const signInHref = `/session/new?return_to=${encodeURIComponent(
    window.location.pathname + window.location.search,
  )}`;

  return (
    <div
      role="dialog"
      aria-modal={open}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      // The backdrop styling is applied only while open. iOS Safari keeps
      // painting backdrop-filter through visibility: hidden, which blurs the
      // whole page behind a gate nobody asked for.
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
        open ? "bg-background/80 backdrop-blur-xs" : "invisible"
      }`}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="w-full max-w-md rounded-lg border bg-card p-6 text-card-foreground shadow-lg outline-hidden"
      >
        <div className="flex items-start gap-3">
          {failed ? (
            <ShieldAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          ) : (
            <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          )}
          <div className="min-w-0 flex-1 space-y-1">
            <h2 id={titleId} className="text-base font-semibold">
              {failed
                ? "Verification could not complete"
                : "One quick check before you continue"}
            </h2>
            <p id={bodyId} className="text-sm text-muted-foreground">
              {failed
                ? `Cloudflare confirms you are a person before ${toolName} runs, and that check failed. Content blockers and private windows are the usual cause.`
                : `Cloudflare needs to confirm you are a person before ${toolName} runs.`}
            </p>

            {/* The widget stays mounted in every state. Hidden once failed so
                a broken widget is not left on screen next to the way out. */}
            <div
              ref={turnstile.containerRef}
              className={`pt-3 empty:hidden ${failed ? "hidden" : ""}`}
            />

            {failed && (
              <div className="space-y-3 pt-4">
                <p className="text-sm text-muted-foreground">
                  Signed-in users skip the check.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => window.location.reload()}
                  >
                    Reload the page
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <a href={signInHref}>Sign in</a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
